import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateResourceBundle, useDeleteResourceBundle, useResourceBundleList, type BundleSearchField } from "../../api/resources";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useDeviceModels } from "../../api/deviceTypes";
import { DataTable } from "../../components/DataTable";
import { StatusBadge } from "../../components/StatusBadge";
import { extractErrorMessage } from "../../api/errors";
import type { ResourceBundle } from "../../api/types";
import { Rocket, Trash2 } from "lucide-react";

const statusOptions = [
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
];

const searchFields = [
  { value: "name", label: "Name" },
  { value: "model", label: "Model" },
];

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

export function ResourceListPage() {
  const navigate = useNavigate();
  const deleteBundle = useDeleteResourceBundle();
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState<BundleSearchField>("name");
  // Cursors for pages after the first, so Previous steps back one page.
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  const page = cursors.length + 1;
  const debouncedSearch = useDebouncedValue(search, 300);
  const [deleteTarget, setDeleteTarget] = useState<ResourceBundle | null>(null);
  const [deleteError, setDeleteError] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  // Search and status are applied by the server across every page.
  const { data, isLoading, isPlaceholderData } = useResourceBundleList({
    status: statusFilter || undefined,
    cursor,
    search: debouncedSearch,
    searchField,
  });
  const rows = data?.data ?? [];
  const hasMore = !!data?.pagination.hasMore;

  function showNextPage() {
    const next = data?.pagination.cursor;
    if (!next || isPlaceholderData) return;
    setCursors([...cursors, next]);
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleteError("");
    try {
      await deleteBundle.mutateAsync(deleteTarget.bundleId);
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError(extractErrorMessage(err, "Failed to delete bundle"));
    }
  }

  const columns = [
    { key: "name", header: "Name" },
    { key: "status", header: "Status", render: (row: ResourceBundle) => <StatusBadge status={row.status} /> },
    { key: "model", header: "Model" },
    { key: "latestVersion", header: "Latest version", render: (row: ResourceBundle) => (row.latestVersion > 0 ? `v${row.latestVersion}` : "unpublished") },
    { key: "workingSetSize", header: "Working set", render: (row: ResourceBundle) => `${row.fileCount} file${row.fileCount === 1 ? "" : "s"} (${formatSize(row.workingSetSize)})` },
    {
      key: "updatedAt",
      header: "Updated",
      render: (row: ResourceBundle) => new Date(row.updatedAt).toLocaleString(),
    },
    {
      key: "actions",
      header: "",
      width: "80px",
      render: (row: ResourceBundle) => (
        <div className="flex justify-end gap-1">
          {row.latestVersion > 0 && (
            <button
              type="button"
              onClick={() => navigate(`/tasks/create?resourceBundleId=${encodeURIComponent(row.bundleId)}`)}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-riot-navy-700 dark:hover:text-riot-gold-400"
              aria-label={`Deploy ${row.name}`}
              title="Deploy"
            >
              <Rocket size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              setDeleteTarget(row);
              setDeleteError("");
            }}
            className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-300"
            aria-label={`Delete ${row.name}`}
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      {isLoading ? (
        <div className="py-20 text-center text-sm text-gray-400 dark:text-riot-navy-400">Loading resource bundles...</div>
      ) : (
        <DataTable
          title="Resources"
          columns={columns}
          data={rows}
          keyExtractor={(b) => b.bundleId}
          onRowClick={(b) => navigate(`/resources/${b.bundleId}`)}
          serverSide
          emptyMessage={
            hasMore && rows.length === 0 && !isPlaceholderData ? (
              <span>
                No matches in the bundles checked so far.{" "}
                <button type="button" onClick={showNextPage} className="font-medium text-riot-gold-600 hover:text-riot-gold-700 dark:text-riot-gold-400">
                  Keep searching
                </button>
              </span>
            ) : undefined
          }
          filters={{
            searchPlaceholder: "Search by",
            searchFields,
            statusOptions,
          }}
          onFilterChange={(f) => {
            setStatusFilter(f.status);
            setSearch(f.search);
            setSearchField((f.searchField as BundleSearchField) || "name");
            setCursors([]);
          }}
          pagination={{
            page,
            count: rows.length,
            hasMore,
            onNext: showNextPage,
            onPrev: () => setCursors(cursors.slice(0, -1)),
            onFirst: () => setCursors([]),
          }}
          actions={
            <button
              onClick={() => setShowCreate(true)}
              className="rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600"
            >
              New bundle
            </button>
          }
        />
      )}

      {showCreate && (
        <CreateBundleModal onClose={() => setShowCreate(false)} onCreated={(id) => navigate(`/resources/${id}`)} />
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4" role="dialog" aria-modal="true" aria-label="Delete bundle confirmation">
          <div className="w-full max-w-md rounded-lg border border-riot-navy-700 bg-white p-5 shadow-xl dark:bg-riot-navy-900">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Delete bundle?</h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-riot-navy-200">
              {deleteTarget.name} will be marked inactive and removed from bundle lists. Published versions already deployed to devices are unaffected.
            </p>

            {deleteError && <p className="mt-3 text-sm text-red-600 dark:text-red-300">{deleteError}</p>}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteBundle.isPending}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 active:bg-red-800 disabled:opacity-50"
              >
                {deleteBundle.isPending ? "Deleting..." : "Delete bundle"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CreateBundleModal({ onClose, onCreated }: { onClose: () => void; onCreated: (bundleId: string) => void }) {
  const createBundle = useCreateResourceBundle();
  const modelsQuery = useDeviceModels();
  const deviceModels = useMemo(
    () =>
      [...(modelsQuery.data?.data ?? [])]
        .filter((entry) => entry.status !== "deprecated")
        .sort((a, b) => a.name.localeCompare(b.name)),
    [modelsQuery.data?.data],
  );
  const [name, setName] = useState("");
  const [model, setModel] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    try {
      const res = await createBundle.mutateAsync({ name: name.trim(), model: model.trim(), description: description.trim() || undefined });
      onCreated(res.data.bundleId);
    } catch (err) {
      setError(extractErrorMessage(err, "Failed to create bundle"));
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4" role="dialog" aria-modal="true" aria-label="Create resource bundle">
      <form onSubmit={handleSubmit} className="w-full max-w-md rounded-lg border border-riot-navy-700 bg-white p-5 shadow-xl dark:bg-riot-navy-900">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">New resource bundle</h3>
        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-riot-navy-200">Name</label>
            <input aria-label="Bundle name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. web-ui" className="w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white" required />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-riot-navy-200">Model</label>
            <select
              aria-label="Device model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              disabled={modelsQuery.isLoading}
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white disabled:cursor-not-allowed disabled:opacity-60"
              required
            >
              <option value="">{modelsQuery.isLoading ? "Loading models..." : "Select model..."}</option>
              {deviceModels.map((entry) => (
                <option key={entry.modelCode} value={entry.modelCode}>{entry.name}</option>
              ))}
            </select>
            {!modelsQuery.isLoading && deviceModels.length === 0 && (
              <p className="mt-1 text-xs text-gray-500 dark:text-riot-navy-300">
                No device models are configured yet — ask a platform admin to add one under Device Types.
              </p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-riot-navy-200">Description (optional)</label>
            <textarea aria-label="Description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-white" />
          </div>
        </div>

        {error && <p className="mt-3 text-sm text-red-600 dark:text-red-300">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-100 dark:hover:bg-riot-navy-800">
            Cancel
          </button>
          <button type="submit" disabled={!name.trim() || !model.trim() || createBundle.isPending} className="rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-50">
            {createBundle.isPending ? "Creating..." : "Create bundle"}
          </button>
        </div>
      </form>
    </div>
  );
}
